import {useEffect, useState} from 'react'
import {Pressable, View} from 'react-native'
import {ImageBackground} from 'expo-image'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import {FocusGuards, FocusScope} from 'radix-ui/internal'

import {BRAND, BRAND_NAME} from '#/lib/brand'
import {BrandSsoIcon} from '#/lib/brand/BrandMarkSvg'
import {startCooeyLogin} from '#/lib/cooey/handoff'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {Logo} from '#/view/icons/Logo'
import {atoms as a, flatten, useBreakpoints, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {DiscordLogo} from '#/components/icons/DiscordLogo'
import {TimesLarge_Stroke2_Corner0_Rounded as XIcon} from '#/components/icons/Times'
import {Text} from '#/components/Typography'
import {useAnalytics} from '#/analytics'

const welcomeModalBg = require('../../assets/images/welcome-modal-bg.jpg')

interface WelcomeModalProps {
  control: {
    isOpen: boolean
    open: () => void
    close: () => void
  }
}

export function WelcomeModal({control}: WelcomeModalProps) {
  const {_} = useLingui()
  const sso = BRAND.sso
  const ax = useAnalytics()
  const {requestSwitchToAccount} = useLoggedOutViewControls()
  const {gtMobile} = useBreakpoints()
  const [isExiting, setIsExiting] = useState(false)
  const [signInLinkHovered, setSignInLinkHovered] = useState(false)

  /*
   * BRAND-AWARE SURFACE. This modal was written against ONE brand and hardcoded
   * it: a light sky photo, slate ink, and #C8161D links — which is cooey's red,
   * so every other tenant's signed-out first impression showed cooey's accent.
   * (The alf primary re-hue in src/alf/themes.ts fixed the shell but cannot
   * reach literals in here.) On an arcade brand the result was worse than
   * off-palette: a dark arcade shell wrapped around a stock light-blue modal,
   * which is what got x.yeet.fm reported as broken on 2026-08-16.
   *
   * So: links follow BRAND.accent everywhere, and an `arcade` brand swaps the
   * whole surface for its own dark ground — no photo, light ink — instead of
   * borrowing someone else's daylight.
   */
  const arcade = !!BRAND.arcade
  const surface = arcade
    ? {
        card: '#140f24', // deep-space indigo ground (matches splashBg / theme)
        ink: '#f2eefb',
        inkDim: 'rgba(242,238,251,0.72)',
        headlineGradient:
          'linear-gradient(180deg, #ffffff 0%, #cfc6ea 62%, rgba(207,198,234,0.55) 100%)',
      }
    : {
        card: '#C0DCF0',
        ink: '#354358',
        inkDim: '#405168',
        headlineGradient:
          'linear-gradient(180deg, #313F54 0%, #667B99 83.65%, rgba(102, 123, 153, 0.50) 100%)',
      }
  const linkColor = BRAND.accent

  const fadeOutAndClose = (callback?: () => void) => {
    setIsExiting(true)
    setTimeout(() => {
      control.close()
      if (callback) callback()
    }, 150)
  }

  useEffect(() => {
    if (control.isOpen) {
      ax.metric('welcomeModal:presented', {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [control.isOpen])

  const onPressCreateAccount = () => {
    ax.metric('welcomeModal:signupClicked', {})
    control.close()
    requestSwitchToAccount({requestedAccount: 'new'})
  }

  const onPressExplore = () => {
    ax.metric('welcomeModal:exploreClicked', {})
    fadeOutAndClose()
  }

  const onPressSignIn = () => {
    ax.metric('welcomeModal:signinClicked', {})
    control.close()
    requestSwitchToAccount({requestedAccount: 'existing'})
  }

  /**
   * Discord/Authentik SSO — the default sign-in, matching chat. Redirects to
   * delta /auth/oidc/login?flow=atproto -> Authentik (Discord) -> handoff ->
   * atproto session; identity is centralized at Authentik across cooey apps.
   */
  const onPressDiscord = () => {
    ax.metric('welcomeModal:signinClicked', {})
    startCooeyLogin()
  }

  FocusGuards.useFocusGuards()

  return (
    <View
      role="dialog"
      aria-modal
      style={[
        a.fixed,
        a.inset_0,
        a.justify_center,
        a.align_center,
        {zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.2)'},
        web({backdropFilter: 'blur(15px)'}),
        isExiting ? a.fade_out : a.fade_in,
      ]}>
      <FocusScope.FocusScope asChild loop trapped>
        <View
          style={flatten([
            {
              maxWidth: 800,
              maxHeight: 600,
              width: '90%',
              height: '90%',
              backgroundColor: surface.card,
            },
            a.rounded_lg,
            a.overflow_hidden,
            a.zoom_in,
          ])}>
          <ImageBackground
            source={arcade ? null : welcomeModalBg}
            style={[a.flex_1, a.justify_center]}
            contentFit="cover">
            <View style={[a.gap_2xl, a.align_center, a.p_4xl]}>
              <View
                style={[
                  a.flex_row,
                  a.align_center,
                  a.justify_center,
                  a.w_full,
                  a.p_0,
                ]}>
                <View style={[a.flex_row, a.align_center, a.gap_xs]}>
                  <Logo width={26} />
                  <Text
                    style={[
                      a.text_2xl,
                      a.font_semi_bold,
                      a.user_select_none,
                      {color: surface.ink, letterSpacing: -0.5},
                    ]}>
                    {BRAND_NAME}
                  </Text>
                </View>
              </View>
              <View
                style={[
                  a.gap_sm,
                  a.align_center,
                  a.pt_5xl,
                  a.pb_3xl,
                  a.mt_2xl,
                ]}>
                <Text
                  style={[
                    gtMobile ? a.text_4xl : a.text_3xl,
                    a.font_semi_bold,
                    a.text_center,
                    {color: surface.ink},
                    web({
                      backgroundImage: surface.headlineGradient,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      color: 'transparent',
                      lineHeight: 1.2,
                      letterSpacing: -0.5,
                    }),
                  ]}>
                  <Trans>Real people.</Trans>
                  {'\n'}
                  <Trans>Real conversations.</Trans>
                  {'\n'}
                  <Trans>Social media you control.</Trans>
                </Text>
              </View>
              <View style={[a.gap_md, a.align_center]}>
                <View
                  style={[
                    gtMobile ? a.flex_row : undefined,
                    a.gap_md,
                    a.align_center,
                  ]}>
                  {/* Brand SSO to the LEFT of Create account. Hidden on
                      password-only brands (sso: null). */}
                  {sso && (
                    <Button
                      onPress={onPressDiscord}
                      label={_(msg`Continue with ${sso.label}`)}
                      size="large"
                      color="primary"
                      style={[
                        a.gap_sm,
                        {width: 200, backgroundColor: sso.background},
                      ]}>
                      {sso.icon === 'discord' ? (
                        <DiscordLogo />
                      ) : (
                        <BrandSsoIcon size={20} />
                      )}
                      <ButtonText style={[{color: '#fff'}]}>
                        {sso.label}
                      </ButtonText>
                    </Button>
                  )}
                  <Button
                    onPress={onPressCreateAccount}
                    label={_(msg`Create account`)}
                    size="large"
                    color="primary"
                    style={{
                      width: 200,
                      backgroundColor: BRAND.accent,
                    }}>
                    <ButtonText>
                      <Trans>Create account</Trans>
                    </ButtonText>
                  </Button>
                </View>
                <Button
                  onPress={onPressExplore}
                  label={_(msg`Explore the app`)}
                  size="large"
                  color="primary"
                  variant="ghost"
                  style={[a.bg_transparent, {width: 200}]}
                  hoverStyle={[a.bg_transparent]}>
                  {({hovered}) => (
                    <ButtonText
                      style={[hovered && [a.underline], {color: linkColor}]}>
                      <Trans>Explore the app</Trans>
                    </ButtonText>
                  )}
                </Button>
                <View style={[a.align_center, {minWidth: 200}]}>
                  <Text
                    style={[
                      a.text_md,
                      a.text_center,
                      {color: surface.inkDim, lineHeight: 24},
                    ]}>
                    <Trans>Already have an account?</Trans>{' '}
                    <Pressable
                      onPointerEnter={() => setSignInLinkHovered(true)}
                      onPointerLeave={() => setSignInLinkHovered(false)}
                      accessibilityRole="button"
                      accessibilityLabel={_(msg`Sign in`)}
                      accessibilityHint="">
                      <Text
                        style={[
                          a.font_medium,
                          {
                            color: linkColor,
                            fontSize: undefined,
                          },
                          signInLinkHovered && a.underline,
                        ]}
                        onPress={onPressSignIn}>
                        <Trans>Sign in</Trans>
                      </Text>
                    </Pressable>
                  </Text>
                </View>
              </View>
            </View>
            <Button
              label={_(msg`Close welcome modal`)}
              style={[
                a.absolute,
                {
                  top: 8,
                  right: 8,
                },
                a.bg_transparent,
              ]}
              hoverStyle={[a.bg_transparent]}
              onPress={() => {
                ax.metric('welcomeModal:dismissed', {})
                fadeOutAndClose()
              }}
              color="secondary"
              size="small"
              variant="ghost"
              shape="round">
              {({hovered, pressed, focused}) => (
                <XIcon
                  size="md"
                  style={{
                    color: surface.ink,
                    opacity: hovered || pressed || focused ? 1 : 0.7,
                  }}
                />
              )}
            </Button>
          </ImageBackground>
        </View>
      </FocusScope.FocusScope>
    </View>
  )
}
